import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";

const ExternalPlatformSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto").required("Obrigatorio"),
  baseUrl: Yup.string().url("Informe uma URL valida").required("Obrigatorio"),
  token: Yup.string().when("id", {
    is: value => !value,
    then: Yup.string().min(8, "Minimo 8 caracteres").required("Obrigatorio"),
    otherwise: Yup.string()
  }),
  timeoutMs: Yup.number().min(1000).max(120000).required("Obrigatorio")
});

const ExternalPlatformModal = ({ open, onClose, platform, onSaved }) => {
  const initialState = {
    id: "",
    name: "",
    baseUrl: "",
    token: "",
    active: true,
    timeoutMs: 30000,
    note: ""
  };

  const [currentPlatform, setCurrentPlatform] = useState(initialState);

  useEffect(() => {
    if (platform) {
      setCurrentPlatform({
        id: platform.id || "",
        name: platform.name || "",
        baseUrl: platform.baseUrl || "",
        token: "",
        active: platform.active !== false,
        timeoutMs: platform.timeoutMs || 30000,
        note: platform.note || ""
      });
    } else {
      setCurrentPlatform(initialState);
    }
  }, [platform, open]);

  const handleClose = () => {
    setCurrentPlatform(initialState);
    onClose();
  };

  const handleSave = async values => {
    try {
      const payload = {
        name: values.name,
        baseUrl: values.baseUrl,
        active: values.active,
        timeoutMs: Number(values.timeoutMs || 30000),
        note: values.note || null
      };

      if (values.token && values.token.trim()) {
        payload.token = values.token.trim();
      }

      if (values.id) {
        await api.put(`/external-platforms/${values.id}`, payload);
      } else {
        await api.post("/external-platforms", payload);
      }

      toast.success("Plataforma externa salva com sucesso.");
      onSaved();
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {currentPlatform.id ? "Editar plataforma externa" : "Nova plataforma externa"}
      </DialogTitle>
      <Formik
        initialValues={currentPlatform}
        enableReinitialize
        validationSchema={ExternalPlatformSchema}
        onSubmit={(values, actions) => {
          handleSave(values).finally(() => actions.setSubmitting(false));
        }}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Field
                as={TextField}
                label="Nome da plataforma"
                name="name"
                variant="outlined"
                margin="dense"
                fullWidth
                autoFocus
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
              <Field
                as={TextField}
                label="URL da API"
                name="baseUrl"
                variant="outlined"
                margin="dense"
                fullWidth
                placeholder="https://api-outra-plataforma.com"
                error={touched.baseUrl && Boolean(errors.baseUrl)}
                helperText={touched.baseUrl && errors.baseUrl}
              />
              <Field
                as={TextField}
                label={
                  currentPlatform.id
                    ? "Novo token (deixe em branco para manter)"
                    : "Token de integracao"
                }
                name="token"
                type="password"
                variant="outlined"
                margin="dense"
                fullWidth
                error={touched.token && Boolean(errors.token)}
                helperText={touched.token && errors.token}
              />
              <Field
                as={TextField}
                label="Timeout em ms"
                name="timeoutMs"
                type="number"
                variant="outlined"
                margin="dense"
                fullWidth
                error={touched.timeoutMs && Boolean(errors.timeoutMs)}
                helperText={touched.timeoutMs && errors.timeoutMs}
              />
              <Field
                as={TextField}
                label="Observacao"
                name="note"
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                minRows={2}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={values.active}
                    onChange={e => setFieldValue("active", e.target.checked)}
                    color="primary"
                  />
                }
                label="Ativa"
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                variant="outlined"
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <ButtonWithSpinner
                type="submit"
                color="primary"
                variant="contained"
                loading={isSubmitting}
              >
                Salvar
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ExternalPlatformModal;
